import EmblaCarousel from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import { addAutoplayProgressListeners } from './embla-autoplay-progress.js'
import { addNavBtnListeners, addPlayBtnListeners } from './embla-autoplay.js'
import { addDotBtnsAndClickHandlers } from './embla-dot-button.js'

let emblaNodes = [];

function initializeCarousels() {
    const isMobile = window.innerWidth < 768;

    document.querySelectorAll('[data-carousel]').forEach(carousel => {
        const onlyMobile = carousel.dataset.carouselOnlymobile === 'true';
        const existingEmbla = emblaNodes.find(node => node.element === carousel);

        // Check if carousel should be initialized
        const shouldInitialize = !onlyMobile || (onlyMobile && isMobile);

        if (shouldInitialize && !existingEmbla) {
            const options = carousel.dataset.carouselOptions
                ? JSON.parse(carousel.dataset.carouselOptions)
                : {};

            const alignMobile = carousel.dataset.carouselAlignMobile || "center";
            const alignDesktop = carousel.dataset.carouselAlignDesktop || "start";
            options.align = isMobile ? alignMobile : alignDesktop;

            const plugins = [];
            const isAutoPlay = carousel.dataset.carouselAutoplay === 'true';

            if (isAutoPlay) {
                const autoplaySpeed = parseInt(carousel.dataset.carouselAutoplaySpeed) || 3000;
                plugins.push(Autoplay({ playOnInit: true, delay: autoplaySpeed }));
            }

            const embla = EmblaCarousel(carousel.querySelector('.overflow-embla'), options, plugins);
            
            const slides = carousel.querySelector('.overflow-embla > .flex').children;
            const currentSlide = carousel.querySelector('[data-carousel-current]');
            const totalSlides = carousel.querySelector('[data-carousel-total]');

            // Get navigation buttons
            const prevBtnMobile = carousel.querySelector('.md\\:hidden [data-carousel-prev]');
            const nextBtnMobile = carousel.querySelector('.md\\:hidden [data-carousel-next]');
            const prevBtnDesktop = carousel.querySelector('.hidden.md\\:block[data-carousel-prev]');
            const nextBtnDesktop = carousel.querySelector('.hidden.md\\:block[data-carousel-next]');
            const playBtn = carousel.querySelector('[data-carousel-play]');
            const progressNode = carousel.querySelector('[data-carousel-progress]');
            const dotsNode = carousel.querySelector('[data-carousel-dots]');

            // Store cleanup functions
            const cleanupFunctions = [];

            // Add autoplay and navigation listeners if elements exist
            if (playBtn && isAutoPlay) {
                const removePlayListener = addPlayBtnListeners(embla, playBtn);
                cleanupFunctions.push(removePlayListener);
            }

            if (isAutoPlay && (prevBtnMobile || nextBtnMobile || prevBtnDesktop || nextBtnDesktop || dotsNode)) {
                const removeNavListener = addNavBtnListeners(embla,
                    [prevBtnMobile, prevBtnDesktop],
                    [nextBtnMobile, nextBtnDesktop],
                    [dotsNode]
                );
                cleanupFunctions.push(removeNavListener);
            }

            if (progressNode && isAutoPlay) {
                const removeProgressListener = addAutoplayProgressListeners(embla, progressNode);
                cleanupFunctions.push(removeProgressListener);
            }

            if (dotsNode) {
                const removeDotListener = addDotBtnsAndClickHandlers(embla, dotsNode);
                cleanupFunctions.push(removeDotListener);
            }

            const setSelectedSlide = () => {
                const selectedIndex = embla.selectedScrollSnap();
                const useGrayscale = carousel.dataset.carouselGrayscale === 'true';

                Array.from(slides).forEach((slide, index) => {
                    if (index === selectedIndex) {
                        slide.classList.add('is-selected');
                        if (useGrayscale) {
                            slide.classList.remove('opacity-40', 'grayscale', 'blur-sm', 'scale-95');
                            slide.classList.add('opacity-100', 'grayscale-0', 'blur-0', 'scale-100');
                        }
                    } else {
                        slide.classList.remove('is-selected');
                        if (useGrayscale) {
                            slide.classList.remove('opacity-100', 'grayscale-0', 'blur-0', 'scale-100');
                            slide.classList.add('opacity-40', 'grayscale', 'blur-sm', 'scale-95');
                        }
                    }
                });
            };

            if (totalSlides) {
                totalSlides.textContent = embla.scrollSnapList().length;
            }

            const updateCurrent = () => {
                if (currentSlide) {
                    currentSlide.textContent = embla.selectedScrollSnap() + 1;
                }
            };

            const updateButtons = () => {
                const canScrollPrev = embla.canScrollPrev();
                const canScrollNext = embla.canScrollNext();

                [prevBtnMobile, prevBtnDesktop].forEach(btn => {
                    if (btn) btn.disabled = !canScrollPrev;
                });

                [nextBtnMobile, nextBtnDesktop].forEach(btn => {
                    if (btn) btn.disabled = !canScrollNext;
                });
            };

            // Special case for see more details button
            const seeMoreDetails = document.getElementById("see-more-details");

            const handleNavigation = (direction) => {
                if (seeMoreDetails && !seeMoreDetails.classList.contains("hidden")) {
                    seeMoreDetails.classList.add("hidden");
                }
                direction === 'prev' ? embla.scrollPrev() : embla.scrollNext();
            };

            // Store button click handlers for cleanup
            const buttonHandlers = [];

            [
                { btn: prevBtnMobile, dir: 'prev' },
                { btn: nextBtnMobile, dir: 'next' },
                { btn: prevBtnDesktop, dir: 'prev' },
                { btn: nextBtnDesktop, dir: 'next' }
            ].forEach(({ btn, dir }) => {
                if (btn) {
                    const handler = () => handleNavigation(dir);
                    btn.addEventListener('click', handler);
                    buttonHandlers.push({ element: btn, handler });
                }
            });

            // Add select event listener
            const selectHandler = () => {
                updateCurrent();
                setSelectedSlide();
                updateButtons();
            };
            embla.on('select', selectHandler);

            // Initialize states
            updateCurrent();
            setSelectedSlide();
            updateButtons();

            // Store cleanup function
            const cleanup = () => {
                // Remove button click listeners
                buttonHandlers.forEach(({ element, handler }) => {
                    element.removeEventListener('click', handler);
                });

                // Remove select event listener
                embla.off('select', selectHandler);

                // Execute all stored cleanup functions
                cleanupFunctions.forEach(cleanup => cleanup());

                // Destroy Embla instance
                embla.destroy();
            };

            emblaNodes.push({
                element: carousel,
                instance: embla,
                cleanup
            });

        } else if ((!shouldInitialize || !isMobile) && existingEmbla) {
            // Run cleanup before destroying
            existingEmbla.cleanup();
            emblaNodes = emblaNodes.filter(node => node !== existingEmbla);
        }
    });
}

document.addEventListener('DOMContentLoaded', initializeCarousels);

let resizeTimer;
window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(initializeCarousels, 250);
});